import React from 'react'

import { Info } from 'lucide-react'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { SharingMode } from '~/client/graph'
import { ExternalCalendarWorkflowType } from '~/client/src/desktop/enums/ExternalCalendarWorkflowType'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Radio,
  RadioGroup,
} from '~/client/src/shared/components/shadcn-ui/radio-group'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import CalendarLinkInputStore, {
  DateEndOption,
} from '../CalendarLinkInput.store'

interface IProps {
  store: CalendarLinkInputStore
  workflowType: ExternalCalendarWorkflowType
  fromRulesLabelText: string
  allInDateRangeLabelText: string
}

@observer
export default class CalendarOptionsPicker extends React.Component<IProps> {
  public render(): JSX.Element {
    const { workflowType } = this.props

    const { isEnabled, calendarFormSharingMode, calendarDeliverySharingMode } =
      this.store

    const selectedValue = !isEnabled
      ? Localization.translator.dontShare
      : workflowType === ExternalCalendarWorkflowType.FORM
        ? calendarFormSharingMode
        : calendarDeliverySharingMode

    const shouldRenderDateEndOptions =
      workflowType === ExternalCalendarWorkflowType.FORM
        ? calendarFormSharingMode === SharingMode.AllInDateRange
        : calendarDeliverySharingMode === SharingMode.AllInDateRange

    return (
      <>
        <div className="tw:mt-7.5 tw:px-7.5">
          <strong className="tw:block tw:mb-3">
            {workflowType === ExternalCalendarWorkflowType.FORM
              ? Localization.translator.formSharing
              : Localization.translator.deliverySharing}
          </strong>
          <RadioGroup
            className="tw:flex tw:flex-col tw:gap-2"
            value={selectedValue}
            onValueChange={this.onSharingModeChange}
          >
            <label className="tw:inline-flex tw:items-center tw:gap-2 tw:cursor-not-allowed tw:select-none tw:text-muted-foreground">
              <Radio value={Localization.translator.dontShare} disabled />
              <span>{Localization.translator.dontShare}</span>
            </label>
            <label
              className={classList({
                'tw:inline-flex tw:items-center tw:gap-2 tw:cursor-pointer tw:select-none': true,
                'tw:opacity-50 tw:cursor-not-allowed': !isEnabled,
              })}
            >
              <Radio value={SharingMode.FromRules} disabled={!isEnabled} />
              {this.renderFromRulesLabel(
                selectedValue === SharingMode.FromRules,
              )}
            </label>
            <label
              className={classList({
                'tw:inline-flex tw:items-center tw:gap-2 tw:cursor-pointer tw:select-none': true,
                'tw:opacity-50 tw:cursor-not-allowed': !isEnabled,
              })}
            >
              <Radio value={SharingMode.AllInDateRange} disabled={!isEnabled} />
              {this.renderAllInDateRangeLabel(
                selectedValue === SharingMode.AllInDateRange,
              )}
            </label>
          </RadioGroup>
        </div>
        {shouldRenderDateEndOptions && this.renderDateEndOptions(workflowType)}
      </>
    )
  }

  private onSharingModeChange = (value: string) => {
    if (value === SharingMode.FromRules) {
      this.onFromRulesClick()
    } else if (value === SharingMode.AllInDateRange) {
      this.onAllInDateRangeClick()
    }
  }

  private renderAllInDateRangeLabel(isSelected: boolean): JSX.Element {
    const { allInDateRangeLabelText } = this.props
    return (
      <div
        className={classList({
          'tw:flex tw:items-center': true,
          'tw:text-muted-foreground': !isSelected,
        })}
      >
        {allInDateRangeLabelText}
      </div>
    )
  }

  private renderFromRulesLabel(isSelected: boolean): JSX.Element {
    const { fromRulesLabelText, workflowType } = this.props
    const { translator } = Localization
    const hint =
      workflowType === ExternalCalendarWorkflowType.FORM
        ? translator.onlyWorkflowsSatisfyingModuleRulesWillBeShared
        : translator.onlyDeliveriesSatisfyingModuleRulesWillBeShared

    return (
      <div
        className={classList({
          'tw:flex tw:items-center': true,
          'tw:text-muted-foreground': !isSelected,
        })}
      >
        {Localization.translator.shareBasedOnSettingsIn}
        <span className="tw:ml-1 tw:flex tw:items-center tw:underline">
          {fromRulesLabelText}
          <TooltipWrapper
            content={
              <span>
                <strong>{translator.ifSelected}</strong>
                {hint(fromRulesLabelText)}
              </span>
            }
            className="tw:ml-1 tw:flex tw:items-center"
          >
            <Info size={18} className="tw:block tw:text-muted-foreground" />
          </TooltipWrapper>
        </span>
      </div>
    )
  }

  private renderDateEndOptions(
    workflowType: ExternalCalendarWorkflowType,
  ): JSX.Element {
    const {
      setSharingDateRange,
      setFormSharingTo,
      setDeliverySharingTo,
      currentFormSharingEndOption,
      currentDeliverySharingEndOption,
    } = this.store

    const setDateRange =
      workflowType === ExternalCalendarWorkflowType.FORM
        ? setFormSharingTo
        : setDeliverySharingTo

    return (
      <div className="tw:flex tw:items-center tw:gap-2.5 tw:pl-14">
        {Object.values(DateEndOption).map(option => {
          const isActive =
            workflowType === ExternalCalendarWorkflowType.FORM
              ? currentFormSharingEndOption === option
              : currentDeliverySharingEndOption === option

          return (
            <Button
              key={option}
              aria-pressed={isActive}
              variant={isActive ? 'default' : 'outline'}
              className="tw:flex-1"
              onClick={() =>
                setSharingDateRange(setDateRange, workflowType, option)
              }
            >
              {option}
            </Button>
          )
        })}
      </div>
    )
  }

  private onAllInDateRangeClick = (): void => {
    const { changeCalendarFormSharingMode, changeCalendarDeliverySharingMode } =
      this.store
    if (this.props.workflowType === ExternalCalendarWorkflowType.FORM) {
      changeCalendarFormSharingMode(SharingMode.AllInDateRange)
    } else {
      changeCalendarDeliverySharingMode(SharingMode.AllInDateRange)
    }
  }

  private onFromRulesClick = (): void => {
    const { changeCalendarFormSharingMode, changeCalendarDeliverySharingMode } =
      this.store
    if (this.props.workflowType === ExternalCalendarWorkflowType.FORM) {
      changeCalendarFormSharingMode(SharingMode.FromRules)
    } else {
      changeCalendarDeliverySharingMode(SharingMode.FromRules)
    }
  }

  private get store(): CalendarLinkInputStore {
    return this.props.store
  }
}
